<script setup lang="ts">
import SectionAuthorize from '@sections/section-authorize.vue';
import FormField from "@form/form-field.vue";
import FormOtpInput from "@form/form-otp-input.vue";
import ButtonPrimary from '@buttons/button-primary.vue';
import PartSocialLogin from '@parts/part-social-login.vue';
import PartIconCircle from '@parts/part-icon-circle.vue';
import CoreLoader from "@/components/core/core-loader.vue";
import CoreTimer from "@/components/core/core-timer.vue";
import CoreIcon from "@/components/core/core-icon.vue";
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from "@/store/user";
import { useAuthApi } from "@/store/api/authApi";
import { useRoute, useRouter } from 'vue-router';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useToast } from "vue-toast-notification";
import { SocialProvider } from "@store/api/interfaces";
import { ApiError } from '@/composables/useApi';
import { usePaymentFlowStore } from '@store/paymentFlow';
import { usePaymentApi } from '@store/api/paymentApi';
import { getAutoSentRemaining, markAutoSent, clearAutoSent, getRetryAfterFromError } from '@composables/otpAutoSend';
import { handoffCode, authErrorCode } from '@composables/handoffCapture';
import { consumeHandoffCode } from '@composables/handoff';
import { startProviderAuth, providerFromQuery } from '@composables/oauthEntry';
import isCheckoutPopup from '@/utils/isCheckoutPopup';

const toast = useToast();
const userStore = useUserStore();
const authApi = useAuthApi();
const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const paymentFlow = usePaymentFlowStore();
const paymentApi = usePaymentApi();

const step = ref<'email' | 'otp'>('email');
// Visitors arriving at `/login?provider=…` never use the form: the page only
// records the ad session's cookies and hands them to the provider. Showing the
// form for that moment would flash a screen they cannot act on.
const isRedirectingToProvider = ref(false);
const sendOtpLoading = ref(false);
const verifyOtpLoading = ref(false);
const otpError = ref(false);
const otpCode = ref('');
const canResend = ref(false);
const timerKey = ref(0);
const timerDuration = ref(60);

const loginForm = ref({
	email: '',
});

const v$ = useVuelidate({
	email: { required, email },
}, loginForm);

const fields = computed(() => ({
	header: {
		icon: '/images/account-icon.svg',
		title: t('login.title'),
	},
	form: {
		login: {
			label: t('login.login_field.label'),
			placeholder: t('login.login_field.placeholder')
		},
		button: t('login.login_button'),
		socials: {
			label: t('login.login_with'),
		}
	}
}));

// Carries the backend's error code (a key under `errorCode.*`) so the catch
// below can show the user what actually went wrong instead of a bare redirect.
class AuthCallbackError extends Error {
	constructor(public readonly code: string) {
		super(`OAuth callback failed: ${code}`);
	}
}

// Resolves to true when the backend rejected the code (it is forgotten then)
const applyPromoAfterSocialLogin = async (): Promise<boolean> => {
	if (!paymentFlow.promoCode) return false;
	try {
		await paymentApi.http.applyPromoCode(paymentFlow.promoCode);
		toast.success(t('payment.promo_applied'), { position: 'top' });
	} catch (e) {
		console.error('Failed to apply promo after social login', e);
		const code = paymentApi.methods.promoRejectionCode(e);
		if (code) {
			paymentFlow.clearPromo();
			toast.error(t(`errorCode.${code}`), { position: 'top' });
			return true;
		}
	}
	return false;
};

const redirectAfterSocialLogin = (promoRejected: boolean) => {
	const welcomeRedirect = sessionStorage.getItem('welcome-redirect');
	if (welcomeRedirect) {
		sessionStorage.removeItem('welcome-redirect');
		// The saved path carries ?promo= from before the login: a rejected code
		// must not come back with it
		const target = router.resolve(welcomeRedirect);
		if (promoRejected && target.query.promo) {
			const { promo: _promo, ...query } = target.query;
			router.push({ path: target.path, query, hash: target.hash });
			return;
		}
		router.push(welcomeRedirect);
	} else {
		router.push({ name: 'Home' });
	}
};

const authCallbackHandler = async (provider: SocialProvider) => {
	try {
		// A successful OAuth return carries nothing but a one-time code in the
		// fragment (`#handoff=<code>`), already captured and stripped from the
		// address at startup — no token, no profile, no query at all. Its
		// absence therefore means the attempt failed, and the backend put the
		// reason in `?code=` (captured alongside).
		if (!handoffCode) {
			throw new AuthCallbackError(authErrorCode ?? 'auth_by_provider_failed');
		}

		// In the checkout popup the code is forwarded, not spent: it is
		// single-use and the session belongs to the opener.
		if (isCheckoutPopup()) {
			window.opener.postMessage({
				type: 'auth-result',
				success: true,
				handoffCode,
				provider
			}, window.location.origin);
			window.close();
			return;
		}

		const established = await consumeHandoffCode(handoffCode, provider);
		if (!established) {
			throw new AuthCallbackError('handoff_invalid');
		}

		const promoRejected = await applyPromoAfterSocialLogin();
		redirectAfterSocialLogin(promoRejected);
	} catch (error) {
		console.error('ErrorProvideAuth', error);

		// If popup — send error back
		if (isCheckoutPopup()) {
			window.opener.postMessage({
				type: 'auth-result',
				success: false
			}, window.location.origin);
			window.close();
			return;
		}

		const code = error instanceof ApiError || error instanceof AuthCallbackError
			? error.code
			: null;
		if (code) {
			toast.error(t(`errorCode.${code}`), {
				position: 'top'
			});
		}
		router.push({ name: 'Login' });
	}
};

const onSendOtp = async () => {
	try {
		if (sendOtpLoading.value) return;

		const validate = await v$.value.$validate();
		if (!validate) return;

		sendOtpLoading.value = true;
		const retryAfter = await authApi.methods.sendOtp(loginForm.value.email);
		timerDuration.value = retryAfter || 60;
		step.value = 'otp';
		otpCode.value = '';
		otpError.value = false;
		canResend.value = false;
		timerKey.value++;
	} catch (error) {
		console.error('SendOtpError', error);
		if (error instanceof ApiError) {
			// On a resend-cooldown 429, a code was already sent — move to the
			// code step and start the exact server countdown from `retry_after`.
			const retryAfter = error.code === 'otp_rate_limited' ? getRetryAfterFromError(error) : null;
			if (retryAfter) {
				step.value = 'otp';
				timerDuration.value = retryAfter;
				canResend.value = false;
				timerKey.value++;
			}
			toast.error(t(`errorCode.${error.code}`), {
				position: 'top'
			});
		}
	} finally {
		sendOtpLoading.value = false;
	}
};

const onVerifyOtp = async () => {
	try {
		if (verifyOtpLoading.value) return;
		if (otpCode.value.length < 6) return;

		verifyOtpLoading.value = true;
		otpError.value = false;

		await userStore.methods.login({
			email: loginForm.value.email,
			code: otpCode.value,
			lang: locale.value,
		});
		clearAutoSent();
		router.push({ name: 'Home' });
	} catch (error) {
		console.error('VerifyOtpError', error);
		otpError.value = true;
		if (error instanceof ApiError) {
			toast.error(t(`errorCode.${error.code}`), {
				position: 'top',
        pauseOnHover: true
			});
		} else {
			toast.error(t('login.otp.error'), {
				position: 'top',
        pauseOnHover: true
			});
		}
	} finally {
		verifyOtpLoading.value = false;
	}
};

const onTimerEnd = () => {
	canResend.value = true;
};

const onResendOtp = async () => {
	try {
		if (sendOtpLoading.value) return;
		sendOtpLoading.value = true;
		const retryAfter = await authApi.methods.sendOtp(loginForm.value.email);
		timerDuration.value = retryAfter || 60;
		canResend.value = false;
		timerKey.value++;
		otpCode.value = '';
		otpError.value = false;
	} catch (error) {
		console.error('ResendOtpError', error);
		if (error instanceof ApiError) {
			// Rate-limited resend: restart the countdown with the exact server value.
			const retryAfter = error.code === 'otp_rate_limited' ? getRetryAfterFromError(error) : null;
			if (retryAfter) {
				timerDuration.value = retryAfter;
				canResend.value = false;
				timerKey.value++;
			}
			toast.error(t(`errorCode.${error.code}`), {
				position: 'top'
			});
		}
	} finally {
		sendOtpLoading.value = false;
	}
};

const goBackToEmail = () => {
	step.value = 'email';
	otpCode.value = '';
	otpError.value = false;
};

// Prefill email passed by the marketing landing (?email=...) and jump
// straight to the code-entry step. Invalid values are silently ignored.
const handleEmailFromQuery = async () => {
	const rawEmail = route.query.email;
	const emailFromQuery = (Array.isArray(rawEmail) ? rawEmail[0] : rawEmail)?.trim();
	if (!emailFromQuery) return;

	loginForm.value.email = emailFromQuery;
	const valid = await v$.value.$validate();
	v$.value.$reset();
	if (!valid) {
		loginForm.value.email = '';
		return;
	}

	const remaining = getAutoSentRemaining(emailFromQuery);
	if (remaining !== null) {
		// Code was already auto-sent in this tab (page reload) — restore
		// the OTP step with the remaining resend timer instead of resending
		step.value = 'otp';
		timerDuration.value = remaining;
		canResend.value = remaining <= 0;
	} else {
		await onSendOtp();
		if (step.value === 'otp') {
			markAutoSent(emailFromQuery, timerDuration.value);
		}
	}
};

onMounted(async () => {
	const { callback } = route.params;
	if (!!callback && typeof callback === "string") {
		await authCallbackHandler(callback as SocialProvider);
		return;
	}

	// `/login?provider=…` is the OAuth entry point used by the marketing
	// landing: starting the flow from this domain lets the ad session's cookies
	// be written here before the hop to the API host, which is a different
	// eTLD+1 and outside the cross-domain linker's reach.
	const provider = providerFromQuery(route.query.provider);
	if (provider) {
		isRedirectingToProvider.value = true;
		await startProviderAuth(provider, route.query);
		return;
	}

	await handleEmailFromQuery();
});
</script>
<template>
	<main class="page page-login">
		<section-authorize
			v-if="!route.params?.callback && !isRedirectingToProvider"
			:fields="fields"
		>
			<!-- Step 1: Email -->
			<transition
				v-if="step === 'email'"
				appear
				name="show-up-container-items"
				:style="`--animation-delay: 0s;
						--animation-item-delay: 0.1s;
						--animation-duration: 0.5s;
						--animation-count: ${3}`"
			>
				<form
					action=""
					class="page-login__form"
					@submit.prevent="onSendOtp"
				>
					<form-field
						name="email"
						v-model="loginForm.email"
						:label="fields.form.login.label"
						:placeholder="fields.form.login.placeholder"
						:error='v$.email?.$errors && v$.email.$errors.length > 0'
						label-font="font-regular"
						class="page-login__field"
					/>

					<button-primary
						:loading="sendOtpLoading"
						:icon="{name: 'arrowRight', viewBox: '0 0 40 40'}"
						class="page-login__button"
					>
						{{ fields.form.button }}
					</button-primary>

					<part-social-login
						v-bind="fields.form.socials"
						class="page-login__social"
					/>
				</form>
			</transition>

			<!-- Step 2: OTP Code -->
			<transition
				v-else
				appear
				name="show-up-container-items"
				:style="`--animation-delay: 0s;
						--animation-item-delay: 0.1s;
						--animation-duration: 0.5s;
						--animation-count: ${4}`"
			>
				<form
					action=""
					class="page-login__form"
					@submit.prevent="onVerifyOtp"
				>
					<form-field
						name="email"
						:model-value="loginForm.email"
						:label="fields.form.login.label"
						disabled
						label-font="font-regular"
						class="page-login__field"
					/>

					<div class="page-login__otp-section">
						<div class="page-login__otp-header">
							<h3 class="page-login__otp-title">{{ $t('login.otp.title') }}</h3>
							<p class="page-login__otp-subtitle">{{ $t('login.otp.subtitle') }}</p>
						</div>
						<form-otp-input
							v-model="otpCode"
							:error="otpError"
						/>
					</div>

					<div class="page-login__resend">
						<template v-if="canResend">
							<span
								class="page-login__resend-link"
								@click="onResendOtp"
							>
								<core-icon
									name="restart"
									viewBox="0 0 20 20"
									class="page-login__resend-icon"
								/>
								{{ $t('login.otp.resend') }}
							</span>
						</template>
						<template v-else>
							<span class="page-login__resend-timer">
								{{ $t('login.otp.resend_timer') }}
								<core-timer
									:key="timerKey"
									:duration="timerDuration"
									@end="onTimerEnd"
								/>
							</span>
						</template>
					</div>

					<div class="page-login__buttons-row">
						<part-icon-circle
							light
							class="page-login__back-button"
							:icon="{ name: 'arrowLeft', viewBox: '0 0 13 22' }"
							@click="goBackToEmail"
						/>
						<button-primary
							:loading="verifyOtpLoading"
							:disabled="otpCode.length < 6"
							:icon="{name: 'arrowRight', viewBox: '0 0 40 40'}"
							class="page-login__button page-login__button--otp"
						>
							{{ fields.form.button }}
						</button-primary>
					</div>
				</form>
			</transition>
		</section-authorize>
		<core-loader
			v-else
			:title="isRedirectingToProvider ? t('common.loader.auth_redirect') : ''"
		/>
	</main>
</template>

<style lang="scss">
.page-login {
	&__form {
		display: flex;
		flex-direction: column;
		gap: 2.4rem;
	}

	&__button {
		width: 100%;
		@include min-desktop {
			height: 6.2rem;
		}

		&--otp {
			flex: 1;
		}
	}

	&__otp-section {
		display: flex;
		flex-direction: column;
		gap: 2.4rem;
	}

	&__otp-header {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	&__otp-title {
		@include font-18-pro-medium;
		@include min-desktop {
			@include font-24-pro-bold;
		}
	}

	&__otp-subtitle {
		color: rgba(var(--color-text-rgb), 0.5);
		@include font-16-pro-regular;
		@include min-desktop {
			@include font-16-pro-regular;
		}
	}

	&__resend {
		text-align: center;
	}

	&__resend-link {
		color: var(--color-text);
		cursor: pointer;
		@include font-16-pro-medium;
		transition: opacity ease 0.2s;
		display: inline-flex;
		align-items: center;
		gap: 0.6rem;

		&:hover {
			opacity: 0.7;
		}
	}

	&__resend-icon {
		width: 2rem;
		height: 2rem;
		stroke: var(--color-text);
		fill: none;
	}

	&__resend-timer {
		color: rgba(var(--color-text-rgb), 0.5);
		@include font-16-pro-regular;

		.core-timer {
			display: inline;
		}
	}

	&__buttons-row {
		display: flex;
		gap: 1.6rem;
		align-items: center;
	}

	&__back-button {
		--button-size: 5.2rem;
		--button-icon-size: 0.8rem;
		flex: 0 0 auto;
		cursor: pointer;

		@include min-desktop {
			--button-size: 6.2rem;
		}
	}
}
</style>
